import { ThemeEvents } from '@theme/events';

/** @typedef {'onetime' | 'subscription'} PurchaseOption */

/** @type {PurchaseOption} */
let purchaseOption = 'onetime';

/** @type {number | string | null} */
let sellingPlanId = null;

/** @type {Array<() => void>} */
let removeListeners = [];

const SALE_PRICE_CLASS = '[&_.price]:text-(--color-sale-price)';

/**
 * @param {unknown} option
 * @returns {PurchaseOption}
 */
function normalizePurchaseOption(option) {
  if (typeof option === 'string') {
    return option === 'subscription' ? 'subscription' : 'onetime';
  }

  if (option && typeof option === 'object') {
    const value =
      /** @type {{ value?: string; type?: string; purchaseOption?: string }} */ (option).value ??
      /** @type {{ value?: string; type?: string; purchaseOption?: string }} */ (option).type ??
      /** @type {{ value?: string; type?: string; purchaseOption?: string }} */ (option).purchaseOption;

    return value === 'subscription' ? 'subscription' : 'onetime';
  }

  return 'onetime';
}

/**
 * @returns {string | undefined}
 */
function getCurrentVariantId() {
  const variantInput = document.querySelector('product-form-component input[name="id"]');
  return variantInput instanceof HTMLInputElement ? variantInput.value : undefined;
}

/**
 * @returns {NodeListOf<HTMLElement>}
 */
function getAddToCartPriceContainers() {
  return document.querySelectorAll('.add-to-cart-button__price [ref="priceContainer"]');
}

function cacheBasePrices() {
  getAddToCartPriceContainers().forEach((container) => {
    if (container.dataset.basePriceHtml) return;

    container.dataset.basePriceHtml = container.innerHTML;
    container.dataset.basePriceClass = container.className;
  });
}

function clearBasePriceCache() {
  getAddToCartPriceContainers().forEach((container) => {
    delete container.dataset.basePriceHtml;
    delete container.dataset.basePriceClass;
  });
}

/**
 * @param {HTMLElement} container
 * @param {string} regularPrice
 * @param {string} salePrice
 */
function renderSubscriptionSalePrice(container, regularPrice, salePrice) {
  const regularLabel = Theme.translations.price_regular ?? 'Regular price';
  const saleLabel = Theme.translations.price_sale ?? 'Sale price';

  container.innerHTML = `
    <span role="group">
      <span class="visually-hidden">${regularLabel}&nbsp;</span>
      <span class="compare-at-price">${regularPrice}</span>
    </span>
    <span role="group">
      <span class="visually-hidden">${saleLabel}&nbsp;</span>
      <span class="price">${salePrice}</span>
    </span>
  `;

  if (!container.classList.contains(SALE_PRICE_CLASS)) {
    container.classList.add(SALE_PRICE_CLASS);
  }
}

/**
 * @param {HTMLElement} container
 */
function restoreBasePrice(container) {
  if (container.dataset.basePriceHtml) {
    container.innerHTML = container.dataset.basePriceHtml;
    container.className = container.dataset.basePriceClass ?? '';
    return;
  }

  const variantId = getCurrentVariantId();
  const unitPrice = variantId
    ? window.SubscriptionWidgetConfig?.formattedPrices?.[variantId]?.unit
    : undefined;

  if (unitPrice) {
    container.innerHTML = `<span class="price">${unitPrice}</span>`;
    container.classList.remove(SALE_PRICE_CLASS);
  }
}

function updateAddToCartPrices() {
  const variantId = getCurrentVariantId();
  if (!variantId) return;

  const variantPrices = window.SubscriptionWidgetConfig?.formattedPrices?.[variantId];
  const containers = getAddToCartPriceContainers();

  if (!variantPrices || containers.length === 0) return;

  if (purchaseOption === 'subscription') {
    const planPricing = sellingPlanId ? variantPrices.sellingPlans?.[sellingPlanId] : null;
    const salePrice = planPricing?.price ?? variantPrices.discounted;
    const regularPrice = variantPrices.unit;

    if (salePrice && regularPrice && salePrice !== regularPrice) {
      containers.forEach((container) => {
        renderSubscriptionSalePrice(container, regularPrice, salePrice);
      });
      return;
    }
  }

  containers.forEach((container) => {
    restoreBasePrice(container);
  });
}

/**
 * @param {import('@theme/events').VariantUpdateEvent} event
 */
function handleVariantUpdate(event) {
  const productForm = document.querySelector('product-form-component');
  if (!productForm || event.detail.data.productId !== productForm.dataset.productId) return;

  requestAnimationFrame(() => {
    clearBasePriceCache();
    cacheBasePrices();
    updateAddToCartPrices();
  });
}

/**
 * @param {object} api
 */
function registerRechargeApi(api) {
  removeListeners.forEach((remove) => remove());
  removeListeners = [];

  removeListeners.push(
    api.events.onPurchaseOptionChange((option) => {
      purchaseOption = normalizePurchaseOption(option);
      updateAddToCartPrices();
    })
  );

  removeListeners.push(
    api.events.onSellingPlanChange((planId) => {
      sellingPlanId = planId;
      if (purchaseOption === 'subscription') {
        updateAddToCartPrices();
      }
    })
  );

  removeListeners.push(
    api.events.onVariantChange(() => {
      clearBasePriceCache();
      cacheBasePrices();
      updateAddToCartPrices();
    })
  );

  cacheBasePrices();
  updateAddToCartPrices();
}

const previousReadyCallback = window.RechargeSubscriptionWidgetReady;

window.RechargeSubscriptionWidgetReady = (api) => {
  registerRechargeApi(api);
  previousReadyCallback?.(api);
};

document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
